/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './line-item-diagnostics-page.component.html';
import {StatsResource} from '../../components/api/resources/resource/stats-resources/stats-resource';
import {BidsService} from '../../components/api/resources/resource/bids.service';
import {DiagnosticRequestExecutionResource} from '../../components/api/resources/resource/execution-resources/diagnostic-request-execution.resource';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

import {interpolateTemplate} from '../common/filters/interpolate-template';

@Component({
  standalone: false,
  selector: 'adn-line-item-diagnostics-page',
  template
})
export class LineItemDiagnosticsPageComponent implements OnInit, OnDestroy {
  @Input() model: any;

  launchDisabled = false;
  showBidUpdates = false;
  isAxAdvertiser = false;
  infoReset: any = {};
  results: any = {messages: []};
  hasBidResults = false;
  bidProcessing = false;
  siteBids: any[] = [];
  sites: any[] = [];

  private readonly intervalRepeat = 40;
  private request: any;
  private intervalPromise: any;
  private intervalCounter = 0;
  private intervalTime = 1250;

  constructor(
    private readonly statsResource: StatsResource,
    private readonly ngZone: NgZone,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly Bids: BidsService,
    @Inject(DiagnosticRequestExecutionResource) private readonly DiagnosticRequestExecution: any
  ) {
  }

  ngOnInit(): void {
    this.showBidUpdates = this.model?.bidSpecification?.bidStrategy === 'DYNAMIC';
    this.isAxAdvertiser = this.LocalUserPermissions.isAxAdvertiser();
    this.initData();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalPromise);
  }

  getFloorPrices(): void {
    if (this.infoReset.info) {
      this.infoReset.info();
    }
  }

  getBidUpdates(): void {
    this.bidProcessing = true;
    this.hasBidResults = false;
    const statsStartDate = moment.utc().subtract(1, 'd');
    this.statsResource.get({
      lineItemId: this.model.id,
      startDate: statsStartDate,
      groupBy: 'SITE'
    }, (response) => {
      this.ngZone.run(() => {
        this.sites = _.sortBy(response.chunks, ['totalCost.amount']).reverse().map((chunk) => {
          const site = chunk.site;
          site.cost = chunk.totalCost;
          return site;
        });
        this.Bids.bidUpdates(this.model.id).then((data) => {
          this.ngZone.run(() => {
            this.siteBids = [];
            if (_.isArray(data)) {
              const bidsBySite: Record<string, any> = {};
              data.forEach((siteUpdates) => {
                siteUpdates.updates = siteUpdates.updates.filter((bid) => moment(bid.time) > statsStartDate);
                if (siteUpdates.site && siteUpdates.site.id) {
                  bidsBySite[siteUpdates.site.id] = siteUpdates;
                }
              });
              this.sites.forEach((site) => {
                const siteBids = bidsBySite[site.id];
                if (siteBids) {
                  siteBids.cost = site.cost;
                  this.siteBids.push(siteBids);
                }
              });
            }
            this.hasBidResults = true;
            this.bidProcessing = false;
          });
        }).catch(() => {
          this.ngZone.run(() => {
            this.bidProcessing = false;
          });
        });
      });
    }, () => {
      this.ngZone.run(() => {
        this.bidProcessing = false;
      });
    }, this);
  }

  launchRequest(): void {
    this.initData();
    this.launchDisabled = true;
    this.DiagnosticRequestExecution.start({
      testType: 'LINE_ITEM',
      lineItem: this.model.id
    }).then((result) => {
      this.ngZone.run(() => {
        this.request = result;
        this.intervalPromise = setInterval(() => this.checkComplete(), this.intervalTime);
        this.intervalTime = 5000;
      });
    }).catch(() => {
      this.ngZone.run(() => this.failDiagnostics());
    });
  }

  private initData(): void {
    clearInterval(this.intervalPromise);
    this.intervalPromise = null;
    this.results = {messages: []};
    this.intervalCounter = 0;
  }

  private checkComplete(): void {
    this.DiagnosticRequestExecution.get({id: this.request.id}).$promise.then((results) => {
      this.ngZone.run(() => {
        results.results.forEach((result) => {
          result.message.apiTranslation = interpolateTemplate(result.message.text)(result.message.parameters);
        });
        this.intervalCounter++;
        if (results.isCompleted()) {
          this.results = results;
          clearInterval(this.intervalPromise);
          this.launchDisabled = false;
        } else if (!results.inProgress() || this.intervalCounter >= this.intervalRepeat) {
          this.results = results;
          this.results.failed = true;
          clearInterval(this.intervalPromise);
          this.launchDisabled = false;
        } else {
          this.results = results;
        }
      });
    }).catch(() => {
      this.ngZone.run(() => this.failDiagnostics());
    });
  }

  private failDiagnostics(): void {
    clearInterval(this.intervalPromise);
    this.intervalPromise = null;
    this.results.failed = true;
    this.launchDisabled = false;
  }
}
